import { useSyncExternalStore } from 'react';

import { Download, FileSignature, FileText, LogOut, Shield, User } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { Link, useNavigate } from 'react-router-dom';

import { useUpdateUserPreferences } from '@alamo/api';
import { useAuth } from '@alamo/auth';
import type { ThemeMode } from '@alamo/types';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
  ThemePicker,
  useTheme,
} from '@alamo/ui';

import { showInstallToast } from '../../components/InstallAppPrompt';
import { getInstallMode, promptInstall, subscribeInstallPrompt } from '../../lib/installPrompt';

/**
 * Topbar avatar dropdown (D-13).
 * - Avatar trigger: 40x40 circular. Phase 02.2.4: always renders initials.
 * - Menu items: Meu Perfil, Seguranca, Meus Dados, Consentimentos, Separator,
 *   Tema (inline ThemePicker with layout thumbnails), Separator, Sair.
 */
export function UserMenuDropdown() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const { t } = useTranslation(['auth', 'ui']);
  const { mode, setMode } = useTheme();
  const updatePreferences = useUpdateUserPreferences();

  // Acesso permanente à instalação (não-fixo na topbar). Reativo: aparece quando
  // instalável e some no modo standalone / plataforma sem caminho.
  const installMode = useSyncExternalStore(subscribeInstallPrompt, getInstallMode, getInstallMode);

  // Optimistic apply, then persist to the server.
  const handleSelectTheme = (next: ThemeMode) => {
    setMode(next);
    updatePreferences.mutate({ theme: { mode: next } });
  };

  const initials = (user?.name ?? '?')
    .split(' ')
    .map((word) => word[0] ?? '')
    .filter(Boolean)
    .slice(0, 2)
    .join('')
    .toUpperCase();

  const handleLogout = async () => {
    await logout();
    navigate('/login', { replace: true });
  };

  return (
    <DropdownMenu>
      <DropdownMenuTrigger>
        <button
          type="button"
          className="size-10 rounded-full overflow-hidden focus:outline-none focus-visible:ring-2 focus-visible:ring-secondary-800"
          aria-label={t('auth:user_menu.aria_label')}
        >
          <span className="flex size-10 items-center justify-center bg-primary-500 text-white text-sm font-bold">
            {initials || '?'}
          </span>
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-64">
        <DropdownMenuItem asChild>
          <Link to="/profile" className="flex items-center gap-2">
            <User className="size-4 text-slate-500" aria-hidden />
            {t('auth:user_menu.profile')}
          </Link>
        </DropdownMenuItem>
        <DropdownMenuItem asChild>
          <Link to="/security/2fa" className="flex items-center gap-2">
            <Shield className="size-4 text-slate-500" aria-hidden />
            {t('auth:user_menu.security')}
          </Link>
        </DropdownMenuItem>
        <DropdownMenuItem asChild>
          <Link to="/my-data" className="flex items-center gap-2">
            <FileText className="size-4 text-slate-500" aria-hidden />
            {t('auth:user_menu.my_data')}
          </Link>
        </DropdownMenuItem>
        <DropdownMenuItem asChild>
          <Link to="/consents" className="flex items-center gap-2">
            <FileSignature className="size-4 text-slate-500" aria-hidden />
            {t('auth:user_menu.consents')}
          </Link>
        </DropdownMenuItem>
        {installMode !== 'none' && (
          <DropdownMenuItem
            className="flex items-center gap-2"
            onClick={() => {
              if (installMode === 'prompt') void promptInstall();
              else showInstallToast('ios-safari', t);
            }}
          >
            <Download className="size-4 text-slate-500" aria-hidden />
            {t('ui:pwa.install_action')}
          </DropdownMenuItem>
        )}
        <DropdownMenuSeparator />
        {/* Theme section — inline picker (the DropdownMenu wrapper has no
            submenu/flyout primitive; the cards live outside a DropdownMenuItem
            so selecting a theme applies live without closing the menu). */}
        <DropdownMenuLabel>{t('auth:user_menu.theme')}</DropdownMenuLabel>
        <div className="px-1.5 pb-1.5">
          <ThemePicker mode={mode} onSelect={handleSelectTheme} orientation="horizontal" />
        </div>
        <DropdownMenuSeparator />
        <DropdownMenuItem
          destructive
          onClick={handleLogout}
          className="text-error-600 hover:bg-error-50"
        >
          <LogOut className="size-4" aria-hidden />
          {t('auth:user_menu.sign_out')}
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
